"use client";

import { Brush } from "@visx/brush";
import type { Bounds } from "@visx/brush";
import { scaleLinear, scaleTime } from "@visx/scale";
import { ParentSize } from "@visx/responsive";
import { AreaClosed, LinePath } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { useMemo, useRef } from "react";
import { cn } from "@/lib/utils";

export type ChartBrushSelection =
  | { kind: "time"; start: Date; end: Date }
  | { kind: "band"; startIndex: number; endIndex: number };

export type ChartBrushPreviewSeries = {
  dataKey: string;
  color?: string;
};

export interface ChartBrushProps<T extends Record<string, unknown>> {
  data: T[];
  xDataKey: keyof T & string;
  variant?: "auto" | "time" | "band";
  height?: number;
  className?: string;
  onSelectionChange?: (selection: ChartBrushSelection | null) => void;
  initialSelection?: ChartBrushSelection | null;
  handleSize?: number;
  useWindowMoveEvents?: boolean;
  previewSeries?: ChartBrushPreviewSeries[];
}

const DEFAULT_HANDLE_SIZE = 8;
const DEFAULT_HEIGHT = 48;
const MIN_BRUSH_WIDTH_PX = 6;

function coerceDate(v: unknown): Date | null {
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "number") return new Date(v);
  return null;
}

function detectVariant<T extends Record<string, unknown>>(
  data: T[],
  xDataKey: keyof T & string,
): "time" | "band" {
  const first = data[0];
  if (!first) return "band";
  const v = first[xDataKey];
  if (v instanceof Date) return "time";
  if (typeof v === "string" && coerceDate(v)) return "time";
  return "band";
}

export function ChartBrush<T extends Record<string, unknown>>({
  data,
  xDataKey,
  variant = "auto",
  height = DEFAULT_HEIGHT,
  className,
  onSelectionChange,
  initialSelection,
  handleSize = DEFAULT_HANDLE_SIZE,
  useWindowMoveEvents = true,
  previewSeries,
}: ChartBrushProps<T>) {
  const resolvedVariant = variant === "auto" ? detectVariant(data, xDataKey) : variant;

  return (
    <div className={cn("relative w-full", className)} style={{ height }}>
      <ParentSize>
        {({ width }) =>
          width > 0 && height > 0 ? (
            <ChartBrushInner
              width={width}
              height={height}
              data={data}
              xDataKey={xDataKey}
              variant={resolvedVariant}
              onSelectionChange={onSelectionChange}
              initialSelection={initialSelection}
              handleSize={handleSize}
              useWindowMoveEvents={useWindowMoveEvents}
              previewSeries={previewSeries}
            />
          ) : null
        }
      </ParentSize>
    </div>
  );
}

interface InnerProps<T extends Record<string, unknown>>
  extends Required<Pick<ChartBrushProps<T>, "data" | "xDataKey" | "handleSize" | "useWindowMoveEvents">> {
  width: number;
  height: number;
  variant: "time" | "band";
  onSelectionChange?: (selection: ChartBrushSelection | null) => void;
  initialSelection?: ChartBrushSelection | null;
  previewSeries?: ChartBrushPreviewSeries[];
}

function ChartBrushInner<T extends Record<string, unknown>>({
  width,
  height,
  data,
  xDataKey,
  variant,
  onSelectionChange,
  initialSelection,
  handleSize,
  useWindowMoveEvents,
  previewSeries,
}: InnerProps<T>) {
  const brushRef = useRef<unknown | null>(null);

  const timeScale = useMemo(() => {
    if (variant !== "time") return null;
    const times = data
      .map((d) => coerceDate(d[xDataKey]))
      .filter((d): d is Date => d !== null)
      .map((d) => d.getTime());
    const min = times.length ? Math.min(...times) : 0;
    const max = times.length ? Math.max(...times) : 1;
    return scaleTime({ domain: [new Date(min), new Date(max)], range: [0, width] });
  }, [variant, data, xDataKey, width]);

  const indexScale = useMemo(() => {
    if (variant !== "band") return null;
    return scaleLinear({ domain: [0, Math.max(0, data.length - 1)], range: [0, width] });
  }, [variant, data.length, width]);

  const yScale = useMemo(() => scaleLinear({ domain: [0, 1], range: [height, 0] }), [height]);

  const previewYScale = useMemo(() => {
    if (!previewSeries || previewSeries.length === 0) return null;
    let max = 0;
    for (const s of previewSeries) {
      for (const d of data) {
        const v = Number(d[s.dataKey] ?? 0);
        if (Number.isFinite(v) && v > max) max = v;
      }
    }
    if (max <= 0) max = 1;
    const paddingTop = 3;
    const paddingBottom = 2;
    return scaleLinear({
      domain: [0, max],
      range: [height - paddingBottom, paddingTop],
    });
  }, [previewSeries, data, height]);

  const initialBrushPosition = useMemo(() => {
    if (!initialSelection) return undefined;
    if (initialSelection.kind === "time" && timeScale) {
      return {
        start: { x: timeScale(initialSelection.start) },
        end: { x: timeScale(initialSelection.end) },
      };
    }
    if (initialSelection.kind === "band" && indexScale) {
      return {
        start: { x: indexScale(initialSelection.startIndex) },
        end: { x: indexScale(initialSelection.endIndex) },
      };
    }
    return undefined;
  }, [initialSelection, timeScale, indexScale]);

  const handleChange = (bounds: Bounds | null) => {
    if (!onSelectionChange) return;
    if (!bounds) {
      onSelectionChange(null);
      return;
    }
    if (variant === "time" && timeScale) {
      const rawStart = coerceDate(bounds.x0);
      const rawEnd = coerceDate(bounds.x1);
      if (!rawStart || !rawEnd) {
        onSelectionChange(null);
        return;
      }
      const start = rawStart.getTime() <= rawEnd.getTime() ? rawStart : rawEnd;
      const end = rawStart.getTime() <= rawEnd.getTime() ? rawEnd : rawStart;
      const px0 = timeScale(start);
      const px1 = timeScale(end);
      if (
        typeof px0 === "number" &&
        typeof px1 === "number" &&
        Math.abs(px1 - px0) < MIN_BRUSH_WIDTH_PX
      ) {
        onSelectionChange(null);
        return;
      }
      onSelectionChange({ kind: "time", start, end });
      return;
    }
    if (variant === "band" && indexScale) {
      const rawX0 = typeof bounds.x0 === "number" ? bounds.x0 : Number(bounds.x0);
      const rawX1 = typeof bounds.x1 === "number" ? bounds.x1 : Number(bounds.x1);
      if (!Number.isFinite(rawX0) || !Number.isFinite(rawX1)) {
        onSelectionChange(null);
        return;
      }
      const startIndex = Math.max(0, Math.round(Math.min(rawX0, rawX1)));
      const endIndex = Math.min(
        data.length - 1,
        Math.max(startIndex, Math.round(Math.max(rawX0, rawX1))),
      );
      if (endIndex - startIndex < 1) {
        onSelectionChange(null);
        return;
      }
      onSelectionChange({ kind: "band", startIndex, endIndex });
    }
  };

  const scaleForBrush = timeScale ?? indexScale;
  if (!scaleForBrush) return null;

  const xAccessor = (d: T): number => {
    const raw = d[xDataKey];
    if (variant === "time") {
      const dt = coerceDate(raw);
      const px = dt ? (scaleForBrush as ReturnType<typeof scaleTime>)(dt) : 0;
      return typeof px === "number" ? px : 0;
    }
    const idx = data.indexOf(d);
    const px = (scaleForBrush as ReturnType<typeof scaleLinear>)(idx);
    return typeof px === "number" ? px : 0;
  };

  const yAccessorFor = (dataKey: string) => (d: T): number => {
    const px = previewYScale!(Number(d[dataKey] ?? 0));
    return typeof px === "number" ? px : height;
  };

  return (
    <svg width={width} height={height} className="block">
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={2}
        fill="var(--muted, transparent)"
        opacity={0.35}
      />
      {previewSeries && previewYScale
        ? previewSeries.map((s, i) => {
            const color = s.color ?? "var(--primary)";
            const yAccessor = yAccessorFor(s.dataKey);
            return (
              <g key={`${s.dataKey}-${i}`} style={{ pointerEvents: "none" }}>
                <AreaClosed
                  data={data}
                  x={xAccessor}
                  y={yAccessor}
                  y0={(): number => height}
                  yScale={previewYScale}
                  curve={curveMonotoneX}
                  fill={color}
                  fillOpacity={0.18}
                />
                <LinePath
                  data={data}
                  x={xAccessor}
                  y={yAccessor}
                  curve={curveMonotoneX}
                  stroke={color}
                  strokeWidth={1}
                  strokeOpacity={0.7}
                  fill="none"
                />
              </g>
            );
          })
        : null}
      <Brush
        xScale={scaleForBrush as never}
        yScale={yScale as never}
        width={width}
        height={height}
        margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
        handleSize={handleSize}
        brushDirection="horizontal"
        resizeTriggerAreas={["left", "right"]}
        initialBrushPosition={initialBrushPosition as never}
        onChange={handleChange}
        onBrushEnd={handleChange}
        selectedBoxStyle={{
          fill: "var(--foreground)",
          fillOpacity: 0.08,
          stroke: "var(--chart-brush-border)",
          strokeWidth: 1,
        }}
        disableDraggingSelection={false}
        useWindowMoveEvents={useWindowMoveEvents}
        innerRef={brushRef as never}
      />
    </svg>
  );
}

export default ChartBrush;
